import { SidebarContext } from "@components:Sidebar/Sidebar.Context";
import {
  SidebarMenu,
  SidebarMenuProps,
} from "@components:Sidebar/Sidebar.Menu";
import {
  SidebarMenuItem,
  SidebarMenuItemProps,
} from "@components:Sidebar/Sidebar.MenuItem";
import {
  SidebarSubMenu,
  SidebarSubMenuProps,
} from "@components:Sidebar/Sidebar.SubMenu";
import { Box, SxProps } from "@mui/material";
import { generateSidebarContrastColors } from "@utils:color";
import { FC, ReactNode, useMemo } from "react";

import { Layout } from "@:constants";

interface Sidebar {
  children: ReactNode;
  width?: number;
  collapseWidth?: string;
  isCollapse?: boolean;
  sx?: SxProps;
  /** Sidebar background color - when provided, auto-calculates contrast colors */
  backgroundColor?: string;
  /** Override: selected item background color */
  itemBackgroundColor?: string;
  /** Override: default text color */
  itemTextColor?: string;
  /** Override: selected item text color */
  itemContrastTextColor?: string;
  /** Override: section header text color */
  subHeaderTextColor?: string;
}

interface SidebarComponent extends FC<Sidebar> {
  Menu: FC<SidebarMenuProps>;
  MenuItem: FC<SidebarMenuItemProps>;
  SubMenu: FC<SidebarSubMenuProps>;
}

// Default colors (for sidebars without branding)
const DEFAULT_COLORS = {
  itemBackgroundColor: "#5c6677",
  itemTextColor: "#000000",
  itemContrastTextColor: "#ffffff",
  subHeaderTextColor: "#2b2b2b",
};

export const Sidebar: SidebarComponent = ({
  children,
  width = Layout.SidebarWidth - 35,
  collapseWidth = "80px",
  isCollapse = false,
  sx,
  backgroundColor,
  subHeaderTextColor,
  itemBackgroundColor,
  itemTextColor,
  itemContrastTextColor,
}: Sidebar) => {
  // Auto-calculate contrast colors when backgroundColor provided
  const colors = useMemo(() => {
    if (backgroundColor) {
      const auto = generateSidebarContrastColors(backgroundColor);
      return {
        itemBackgroundColor: itemBackgroundColor ?? auto.selectedBgColor,
        itemTextColor: itemTextColor ?? auto.textColor,
        itemContrastTextColor: itemContrastTextColor ?? auto.contrastTextColor,
        subHeaderTextColor: subHeaderTextColor ?? auto.subHeaderColor,
      };
    }
    // Use provided colors or defaults
    return {
      itemBackgroundColor:
        itemBackgroundColor ?? DEFAULT_COLORS.itemBackgroundColor,
      itemTextColor: itemTextColor ?? DEFAULT_COLORS.itemTextColor,
      itemContrastTextColor:
        itemContrastTextColor ?? DEFAULT_COLORS.itemContrastTextColor,
      subHeaderTextColor:
        subHeaderTextColor ?? DEFAULT_COLORS.subHeaderTextColor,
    };
  }, [
    backgroundColor,
    itemBackgroundColor,
    itemTextColor,
    itemContrastTextColor,
    subHeaderTextColor,
  ]);

  const toggleWidth = isCollapse ? collapseWidth : width;

  return (
    <Box
      className="Sidebar"
      sx={{
        width: toggleWidth,
        flexShrink: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: toggleWidth,
        }}
      >
        <SidebarContext.Provider
          value={{
            width,
            collapseWidth,
            isCollapse,
            ...colors,
          }}
        >
          {children}
        </SidebarContext.Provider>
      </Box>
    </Box>
  );
};

Sidebar.Menu = SidebarMenu;
Sidebar.MenuItem = SidebarMenuItem;
Sidebar.SubMenu = SidebarSubMenu;
